import type { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

interface CacheEntry {
  etag: string;
  data: unknown;
}

const cache = new Map<string, CacheEntry>();
let interceptorsApplied = false;

export function invalidateEtagCache(url: string) {
  cache.delete(url);
}

/** Drop cached GET responses whose URL contains `fragment` (e.g. a form template id). */
export function invalidateEtagCacheContaining(fragment: string) {
  for (const key of cache.keys()) {
    if (key.includes(fragment)) cache.delete(key);
  }
}

export function invalidateFormTemplateEtagCache(formId: string) {
  invalidateEtagCacheContaining(`/api/forms/templates/${formId}`);
}

export function applyEtagInterceptors(instance: AxiosInstance) {
  if (interceptorsApplied) return;
  interceptorsApplied = true;

  // Keyed on the URL *with* its query string: `/api/calendar/day` for another date, location or
  // filter is a different resource, and a 304 must never hand back another one's body.
  const cacheKey = (config: InternalAxiosRequestConfig) => (config.url ? instance.getUri(config) : '');

  instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const entry = cache.get(cacheKey(config));
    if (entry) {
      config.headers = config.headers ?? {};
      config.headers['If-None-Match'] = entry.etag;
    }
    return config;
  });

  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      const etag = response.headers['etag'];
      if (etag && response.config.url) {
        cache.set(cacheKey(response.config), { etag, data: response.data });
      }
      return response;
    },
    (error) => {
      if (error.response?.status === 304 && error.config?.url) {
        const entry = cache.get(cacheKey(error.config));
        if (entry) {
          return Promise.resolve({ ...error.response, data: entry.data, status: 200 });
        }
      }
      return Promise.reject(error);
    },
  );
}
